import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CheckCircle2, CircleAlert, RefreshCw, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMsp } from '@/hooks/useIsMsp';
import { useMspCustomers, useMspIncidentFeed } from '@/features/msp';
import type { MspCustomer, MspCustomerIncident } from '@/types/mspCustomer';
import { RelativeTime } from '@/components/RelativeTime';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { StatTile, type StatTone } from '@/components/ui/stat-tile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  SEVERITY_RANK,
  compareCustomersByUrgency,
  countsOnDashboard,
  healthOf,
  summarizePortfolio,
} from './mspDashboardModel';
import { accountScoped } from '@/lib/activeAccount';

const INCLUDE_PROSPECTS_KEY = 'msp-dashboard-include-prospects';

/** Remembered per browser; on unless this browser turned it off. */
function readIncludeProspects(): boolean {
  try {
    return localStorage.getItem(accountScoped(INCLUDE_PROSPECTS_KEY)) !== 'false';
  } catch {
    // Private mode and hardened settings can throw; counting prospects is the default.
    return true;
  }
}

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

const HEALTH_PRESENTATION: Record<string, { tone: StatTone; label: string; icon: typeof CircleAlert; className: string }> = {
  critical: { tone: 'critical', label: 'Critical', icon: CircleAlert, className: 'bg-sev-1/15 text-sev-1' },
  warning: { tone: 'warning', label: 'Warning', icon: AlertTriangle, className: 'bg-sev-3/20 text-foreground' },
  healthy: { tone: 'good', label: 'Healthy', icon: CheckCircle2, className: 'bg-sev-4/15 text-sev-4' },
};

/**
 * Health as an icon, a word, and a colour — never colour alone, so the board is
 * still readable in greyscale or with any form of colour blindness.
 */
function HealthBadge({ status }: { status: string }) {
  const presentation = HEALTH_PRESENTATION[status] ?? {
    tone: 'neutral',
    label: status,
    icon: CircleAlert,
    className: 'bg-muted text-muted-foreground',
  };
  const Icon = presentation.icon;
  return (
    <Badge className={cn('border-0 font-normal gap-1 capitalize', presentation.className)}>
      <Icon className="w-3 h-3" aria-hidden="true" />
      {presentation.label}
    </Badge>
  );
}

function SeverityDot({ severity }: { severity: string }) {
  const colour =
    severity === 'critical'
      ? 'bg-sev-1'
      : severity === 'high'
        ? 'bg-sev-2'
        : severity === 'medium'
          ? 'bg-sev-3'
          : 'bg-sev-5';
  return (
    <span className="inline-flex items-center gap-1.5 shrink-0">
      <span className={cn('w-1.5 h-1.5 rounded-full', colour)} aria-hidden="true" />
      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground w-14">{severity}</span>
    </span>
  );
}

/**
 * One customer that needs attention today, with the reasons the rollup gave.
 */
function TriageCard({ customer }: { customer: MspCustomer }) {
  const rollup = customer.rollup;
  const status = healthOf(customer);

  return (
    <Link
      to={`/customers/${customer.divisionId}`}
      className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3 hover:border-border-strong transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium truncate">{customer.name}</p>
          <p className="text-xs text-muted-foreground capitalize">
            {customer.lifecycleDisplayName ?? customer.lifecycle ?? 'customer'}
            {customer.tierDisplayName ? ` · ${customer.tierDisplayName}` : ''}
          </p>
        </div>
        <HealthBadge status={status} />
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-semibold tabular-nums">{rollup?.deviceTotal ?? 0}</p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">devices</p>
        </div>
        <div>
          <p className="text-lg font-semibold tabular-nums">
            {rollup ? `${String(rollup.gatewaysOnline)}/${String(rollup.gatewayCount)}` : '0/0'}
          </p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">gateways</p>
        </div>
        <div>
          <p className="text-lg font-semibold tabular-nums">{rollup?.openIncidentCount ?? 0}</p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">incidents</p>
        </div>
      </div>

      {rollup && rollup.healthIssues.length > 0 ? (
        <ul className="space-y-1">
          {rollup.healthIssues.map((issue) => (
            <li key={issue.code} className="text-xs text-muted-foreground flex items-start gap-1.5">
              <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{issue.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Link>
  );
}

export default function MSPDashboard() {
  const { user } = useAuth();
  const organizationId = user?.organizationId;
  const isMsp = useIsMsp();

  const {
    data: customerList,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useMspCustomers(organizationId, isMsp, { includeMetrics: true });
  const customers = customerList?.customers;
  const { data: incidents } = useMspIncidentFeed(organizationId, isMsp);

  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [includeProspects, setIncludeProspects] = useState(readIncludeProspects);
  const changeIncludeProspects = (next: boolean) => {
    setIncludeProspects(next);
    try {
      localStorage.setItem(accountScoped(INCLUDE_PROSPECTS_KEY), String(next));
    } catch {
      // Not remembering the choice is a smaller problem than a thrown error.
    }
  };

  const lifecycles = useMemo(
    () => [...new Set((customers ?? []).map((c) => c.lifecycle).filter((l): l is string => !!l))].sort(),
    [customers],
  );
  const tiers = useMemo(
    () => [...new Set((customers ?? []).map((c) => c.tier).filter((t): t is string => !!t))].sort(),
    [customers],
  );

  const visible = useMemo(() => {
    const filtered = (customers ?? []).filter(
      (c) =>
        (lifecycleFilter === 'all' || c.lifecycle === lifecycleFilter) && (tierFilter === 'all' || c.tier === tierFilter),
    );
    return [...filtered].sort(compareCustomersByUrgency);
  }, [customers, lifecycleFilter, tierFilter]);

  const totals = useMemo(
    () => summarizePortfolio(customers ?? [], { includeProspects }),
    [customers, includeProspects],
  );

  // The numbers, the attention cards and the incident feed cover the same
  // customers: offboarded ones never, prospects while they're included. All
  // customers below still lists everyone.
  const counted = visible.filter((c) => countsOnDashboard(c, includeProspects));
  const triage = counted.filter((c) => healthOf(c) !== 'healthy').slice(0, 6);

  if (!isMsp) {
    return (
      <PageContainer>
        <PageHeader title="Customer dashboard" />
        <EmptyState
          variant="no-data"
          title="MSP mode is off"
          description="Turn on MSP mode under Organization Admin → General to manage divisions as customers."
        />
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title="Customer dashboard" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (isError) {
    // A 403 is an answer, not a failure: say which one it is rather than showing
    // a raw transport error.
    const refusal = (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
    return (
      <PageContainer>
        <PageHeader title="Customer dashboard" />
        {refusal === 'not_an_msp' ? (
          <EmptyState
            variant="no-data"
            title="MSP mode is off"
            description="Turn on MSP mode under Organization Admin → General to manage divisions as customers."
          />
        ) : refusal === 'forbidden' ? (
          <EmptyState
            variant="no-data"
            title="You do not have access to MSP customers"
            description="This needs the MSP customer permission. An administrator can grant it."
          />
        ) : (
          <Alert variant="destructive">
            <AlertDescription>Could not load customers: {error.message}</AlertDescription>
          </Alert>
        )}
      </PageContainer>
    );
  }

  const columns: readonly ResponsiveTableColumn<MspCustomer>[] = [
    {
      key: 'name',
      label: 'Customer',
      hideMobileLabel: true,
      render: (c) => (
        <Link to={`/customers/${c.divisionId}`} className="font-medium hover:underline">
          {c.name}
        </Link>
      ),
    },
    { key: 'health', label: 'Health', render: (c) => <HealthBadge status={healthOf(c)} /> },
    {
      key: 'lifecycle',
      label: 'Lifecycle',
      hideAt: 'mobile',
      render: (c) => (
        <span className="text-xs capitalize text-muted-foreground">
          {c.lifecycleDisplayName ?? c.lifecycle ?? '—'}
        </span>
      ),
    },
    {
      key: 'tier',
      label: 'Tier',
      hideAt: 'mobile',
      render: (c) => <span className="text-xs capitalize text-muted-foreground">{c.tierDisplayName ?? '—'}</span>,
    },
    {
      key: 'devices',
      label: 'Devices',
      align: 'right',
      render: (c) => <span className="text-xs tabular-nums">{c.rollup?.deviceTotal ?? 0}</span>,
    },
    {
      key: 'gateways',
      label: 'Gateways',
      align: 'right',
      render: (c) => (
        <span className="text-xs tabular-nums">
          {c.rollup ? `${String(c.rollup.gatewaysOnline)}/${String(c.rollup.gatewayCount)}` : '—'}
        </span>
      ),
    },
    {
      key: 'incidents',
      label: 'Incidents',
      align: 'right',
      render: (c) =>
        (c.rollup?.openIncidentCount ?? 0) > 0 ? (
          <span className="text-xs tabular-nums text-sev-1">{c.rollup?.openIncidentCount}</span>
        ) : (
          <span className="text-xs tabular-nums text-muted-foreground">0</span>
        ),
    },
  ];

  // Filtered to the customers currently shown: a board filtered to one tier that
  // still lists every tier's incidents underneath is telling two stories at once.
  const visibleDivisionIds = new Set(counted.map((c) => c.divisionId));
  const sortedIncidents: MspCustomerIncident[] = [...(incidents ?? [])]
    .filter((i) => visibleDivisionIds.has(i.divisionId))
    .sort((a, b) => (SEVERITY_RANK[a.severity] ?? 99) - (SEVERITY_RANK[b.severity] ?? 99));

  return (
    <PageContainer>
      <PageHeader
        title="Customer dashboard"
        subtitle={
          totals.prospects > 0
            ? `${plural(totals.customers, 'customer', 'customers')}, ${plural(totals.prospects, 'prospect', 'prospects')}`
            : plural(totals.customers, 'customer', 'customers')
        }
        primary={
          <Button variant="outline" onClick={() => void refetch()} disabled={isFetching} className="gap-2">
            <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} aria-hidden="true" />
            Refresh
          </Button>
        }
      />

      {(customers ?? []).length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No customers yet"
          description="Designate a division as a customer to see it here."
          action={
            <Button asChild>
              <Link to="/admin/divisions">Go to divisions</Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="flex justify-end mb-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <Switch
                checked={includeProspects}
                onCheckedChange={changeIncludeProspects}
                aria-label="Include prospects"
              />
              Include prospects
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatTile
              label="Need attention"
              value={totals.needAttention}
              detail={totals.critical > 0 ? `${String(totals.critical)} critical` : 'nothing critical'}
              tone={totals.critical > 0 ? 'critical' : totals.needAttention > 0 ? 'warning' : 'good'}
              toneLabel={totals.critical > 0 ? 'critical' : totals.needAttention > 0 ? 'warning' : 'healthy'}
            />
            <StatTile
              label="Customers"
              value={totals.customers}
              detail={totals.prospects > 0 ? `+${plural(totals.prospects, 'prospect', 'prospects')}` : undefined}
            />
            <StatTile
              label="Devices"
              value={totals.devices}
              detail={
                totals.devices === 0
                  ? 'none discovered yet'
                  : totals.unreachable > 0
                    ? `${String(totals.unreachable)} unreachable`
                    : 'all reachable'
              }
              tone={totals.unreachable > 0 ? 'warning' : 'neutral'}
              toneLabel={totals.unreachable > 0 ? 'some unreachable' : undefined}
            />
            <StatTile
              label="Gateways"
              value={`${String(totals.gatewaysOnline)}/${String(totals.gateways)}`}
              detail={totals.gateways === 0 ? 'none deployed' : 'online'}
              tone={totals.gateways > 0 && totals.gatewaysOnline < totals.gateways ? 'warning' : 'neutral'}
              toneLabel={totals.gatewaysOnline < totals.gateways ? 'some offline' : undefined}
            />
            <StatTile
              label="Open incidents"
              value={totals.openIncidents}
              detail={
                Object.keys(totals.bySeverity).length > 0
                  ? Object.entries(totals.bySeverity)
                      .sort(([a], [b]) => (SEVERITY_RANK[a] ?? 99) - (SEVERITY_RANK[b] ?? 99))
                      .map(([severity, count]) => `${String(count)} ${severity}`)
                      .join(' · ')
                  : 'none open'
              }
              tone={totals.criticalIncidents > 0 ? 'critical' : totals.openIncidents > 0 ? 'warning' : 'good'}
              toneLabel={totals.criticalIncidents > 0 ? 'critical' : undefined}
            />
          </div>

          {triage.length > 0 ? (
            <section className="mb-6" aria-labelledby="triage-heading">
              <h2 id="triage-heading" className="text-sm font-semibold mb-3">
                Needs attention
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {triage.map((customer) => (
                  <TriageCard key={customer.divisionId} customer={customer} />
                ))}
              </div>
            </section>
          ) : null}

          {sortedIncidents.length > 0 ? (
            <section className="mb-6" aria-labelledby="incidents-heading">
              <h2 id="incidents-heading" className="text-sm font-semibold mb-3">
                Open incidents
              </h2>
              <div className="rounded-lg border border-border bg-card divide-y divide-border">
                {sortedIncidents.slice(0, 8).map((incident) => (
                  <div key={incident.incidentId} className="flex items-center gap-3 p-3">
                    <SeverityDot severity={incident.severity} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{incident.title}</p>
                      <p className="text-xs text-muted-foreground">{incident.customerName}</p>
                    </div>
                    <RelativeTime value={incident.createdAt} className="text-xs text-muted-foreground shrink-0" />
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section aria-labelledby="all-customers-heading">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 id="all-customers-heading" className="text-sm font-semibold">
                All customers
              </h2>
              <div className="flex items-center gap-2">
                <Select value={lifecycleFilter} onValueChange={setLifecycleFilter}>
                  <SelectTrigger className="w-36" aria-label="Filter by lifecycle">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All lifecycles</SelectItem>
                    {lifecycles.map((l) => (
                      <SelectItem key={l} value={l} className="capitalize">
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={tierFilter} onValueChange={setTierFilter}>
                  <SelectTrigger className="w-32" aria-label="Filter by tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tiers</SelectItem>
                    {tiers.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <ResponsiveTable<MspCustomer>
              columns={columns}
              rows={visible}
              rowKey={(c) => c.divisionId}
              empty={<EmptyState variant="no-results" title="No customers match these filters" />}
            />
          </section>
        </>
      )}
    </PageContainer>
  );
}
